import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Header } from '../components/Header';
import { ChatMessage } from '../components/ChatMessage';
import { ChatInput } from '../components/ChatInput';
import { TypingIndicator } from '../components/TypingIndicator';
import { useChatStore } from '../store/chatStore';
import { Message, ApiResponse } from '../types';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { useLanguage } from '../i18n/LanguageContext';
import { withAiDisclaimer } from '../utils/aiDisclaimer';
import { 
  MessageSquare, 
  Shield, 
  Zap, 
  FileText,
  Users,
  Clock,
  Star,
  Sparkles,
  ArrowRight,
  CheckCircle,
  HelpCircle,
  ChevronRight
} from 'lucide-react';

const ChatPage: React.FC = () => {
  const { t } = useLanguage();
  const {
    messages,
    isLoading,
    error,
    suggestions,
    addMessage,
    setLoading,
    setError,
    setSessionId,
    setSuggestions,
    clearChat,
  } = useChatStore();

  const [isTyping, setIsTyping] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // 初始化会话
  useEffect(() => {
    const initSession = async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}/chat/init`, {}, {
          withCredentials: true
        });
        setSessionId(response.data.sessionId);
      } catch (error) {
        console.error('Failed to init session:', error);
        setError(t('无法连接到服务器', 'Unable to connect to the server'));
      }
    };

    initSession();
  }, [setSessionId, setError, t]);

  // 滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // 发送消息
  const handleSendMessage = async (content: string) => {
    if (!content.trim() || isLoading) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    const userMessage: Message = {
      id: uuidv4(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };
    addMessage(userMessage);
    setShowWelcome(false);

    setLoading(true);
    setIsTyping(true);
    setError(null);

    try {
      const response = await axios.post<ApiResponse>(
        `${API_BASE_URL}/chat/message`,
        { message: content.trim() },
        {
          withCredentials: true,
          signal: abortControllerRef.current.signal,
        }
      );

      const assistantMessage: Message = {
        id: uuidv4(),
        role: 'assistant',
        content: withAiDisclaimer(response.data.message),
        timestamp: new Date(),
        sources: response.data.sources,
      };
      addMessage(assistantMessage);

      if (response.data.suggestions) {
        setSuggestions(response.data.suggestions);
      }
    } catch (error: any) {
      if (error.name === 'CanceledError') {
        console.log('请求已取消');
      } else {
        console.error('Failed to send message:', error);
        setError(t('发送失败，请稍后重试', 'Message failed. Please try again later.'));
        
        const errorMessage: Message = {
          id: uuidv4(),
          role: 'assistant',
          content: withAiDisclaimer(t('抱歉，我遇到了一些技术问题。请稍后再试。', 'Sorry, I ran into a technical issue. Please try again later.')),
          timestamp: new Date(),
        };
        addMessage(errorMessage);
      }
    } finally {
      setLoading(false);
      setIsTyping(false);
      abortControllerRef.current = null;
    }
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsTyping(false);
      setLoading(false);
    }
  };

  // 修复：新对话功能
  const handleNewChat = async () => {
    try {
      // 清除服务器会话
      await axios.post(`${API_BASE_URL}/chat/clear`, {}, {
        withCredentials: true
      });
      
      // 清空本地状态
      clearChat();
      setSuggestions([]);
      setShowWelcome(true);
      
      // 重新初始化会话
      const response = await axios.post(`${API_BASE_URL}/chat/init`, {}, {
        withCredentials: true
      });
      setSessionId(response.data.sessionId);
      
      console.log('新对话已创建');
    } catch (error) {
      console.error('Failed to start new chat:', error);
    }
  };

  const handleHome = () => {
    clearChat();
    setShowWelcome(true);
    setSuggestions([]);
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleSendMessage(suggestion);
  };

  // 统计数据
  const stats = [
    { value: '10,000+', label: t('服务用户', 'Users served'), icon: <Users className="w-5 h-5" /> },
    { value: '50,000+', label: t('对话次数', 'Conversations'), icon: <MessageSquare className="w-5 h-5" /> },
    { value: '98%', label: t('用户满意度', 'Satisfaction'), icon: <Star className="w-5 h-5" /> },
    { value: '24/7', label: t('在线支持', 'Online support'), icon: <Clock className="w-5 h-5" /> }
  ];

  // 核心功能
  const features = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: t('智能问答', 'Smart Q&A'),
      description: t('基于DeepSeek AI，准确回答各类签证问题', 'Powered by DeepSeek AI to answer visa questions accurately.'),
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: t('官方信息', 'Official information'),
      description: t('所有信息基于UKVI官方文档', 'All guidance is based on official UKVI documents.'),
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: t('材料清单', 'Document checklists'),
      description: t('自动生成个性化材料清单', 'Generate personalised application document lists.'),
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: t('准确可靠', 'Reliable answers'),
      description: t('信息来源可追溯', 'Information sources are traceable.'),
    }
  ];

  // 热门签证
  const visaTypes = [
    { name: t('学生签证', 'Student Visa'), prompt: t('学生签证需要什么材料？', 'What documents are needed for a Student Visa?'), icon: '🎓', color: 'from-blue-500 to-cyan-500' },
    { name: t('旅游签证', 'Visitor Visa'), prompt: t('旅游签证需要什么材料？', 'What documents are needed for a Visitor Visa?'), icon: '✈️', color: 'from-green-500 to-emerald-500' },
    { name: t('工作签证', 'Work Visa'), prompt: t('工作签证需要什么材料？', 'What documents are needed for a Work Visa?'), icon: '💼', color: 'from-purple-500 to-pink-500' },
    { name: t('家庭签证', 'Family Visa'), prompt: t('家庭签证需要什么材料？', 'What documents are needed for a Family Visa?'), icon: '👨‍👩‍👧', color: 'from-orange-500 to-red-500' },
  ];

  // 热门问题
  const popularQuestions = [
    t('学生签证需要什么材料？', 'What documents are needed for a Student Visa?'),
    t('旅游签证处理时间多久？', 'How long does a Visitor Visa take?'),
    t('工作签证英语要求', 'English requirements for a Work Visa'),
    t('资金证明要求', 'Financial evidence requirements'),
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f172a]">
      <Header onClearChat={handleNewChat} onHome={handleHome} showNewChat={true} />

      {showWelcome ? (
        // 首页布局
        <div>
          {/* Hero 区域 */}
          <div className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-800 dark:from-indigo-900 dark:via-indigo-800 dark:to-indigo-900 text-white">
            <div className="max-w-6xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
              <div className="text-center max-w-3xl mx-auto">
                <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                  {t('英国签证AI助手', 'UK Visa AI Assistant')}
                </h1>
                <p className="text-xl mb-8 text-indigo-100">
                  {t('让AI技术赋能签证申请，提供准确、智能、个性化的全方位服务', 'AI-powered UK visa guidance that is accurate, smart, and personalised.')}
                </p>
                
                {/* 快速开始卡片 */}
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 sm:p-8">
                  <div className="flex flex-col items-center gap-4">
                    <div className="flex items-center gap-2 text-xl font-semibold">
                      <Sparkles className="w-5 h-5" />
                      <span>{t('快速开始咨询', 'Start quickly')}</span>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-2xl">
                      {popularQuestions.map((question, index) => (
                        <button
                          key={index}
                          onClick={() => handleSendMessage(question)}
                          className="flex items-center justify-between px-4 py-3 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-left group"
                        >
                          <span className="text-sm sm:text-base">{question}</span>
                          <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 统计数据 */}
          <div className="bg-gray-50 dark:bg-[#1e293b] border-b border-gray-200 dark:border-gray-800">
            <div className="max-w-6xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="flex justify-center text-indigo-600 dark:text-indigo-400 mb-2">
                      {stat.icon}
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{stat.value}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 核心功能 */}
          <div className="max-w-6xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-4">{t('核心功能', 'Core Features')}</h2>
            <p className="text-xl text-center text-gray-600 dark:text-gray-400 mb-12 max-w-3xl mx-auto">
              {t('四大核心功能，让签证申请变得简单', 'Four core features that make visa applications easier.')}
            </p>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white dark:bg-[#1e293b] rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all"
                >
                  <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center mb-4 text-indigo-600 dark:text-indigo-400">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 热门签证类型 */}
          <div className="bg-gray-50 dark:bg-[#1e293b] border-y border-gray-200 dark:border-gray-800">
            <div className="max-w-6xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-4">{t('热门签证类型', 'Popular Visa Types')}</h2>
              <p className="text-xl text-center text-gray-600 dark:text-gray-400 mb-12 max-w-3xl mx-auto">
                {t('选择你的签证类型，获取针对性指导', 'Choose your visa type and get targeted guidance.')}
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {visaTypes.map((visa, index) => (
                  <button
                    key={index}
                    onClick={() => handleSendMessage(visa.prompt)}
                    className="group bg-white dark:bg-[#1e293b] rounded-xl p-6 text-center hover:shadow-md transition-all border border-gray-200 dark:border-gray-700"
                  >
                    <span className="text-4xl mb-3 block">{visa.icon}</span>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{visa.name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('点击咨询', 'Ask now')}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 开始使用 */}
          <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{t('开始你的签证之旅', 'Start Your Visa Journey')}</h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
              {t('让 AI 助手帮你轻松搞定英国签证申请', 'Let the AI assistant help you navigate your UK visa application.')}
            </p>
            <button
              onClick={() => handleSendMessage(t('你好', 'Hello'))}
              className="px-8 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors shadow-md hover:shadow-lg inline-flex items-center gap-2"
            >
              <MessageSquare className="w-5 h-5" />
              {t('立即体验', 'Try it now')}
            </button>
          </div>

          {/* 底部版权 */}
          <footer className="bg-gray-900 text-white">
            <div className="max-w-6xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
              <div className="text-center text-sm text-gray-400">
                <p>{t('© 2026 英国签证AI助手. 保留所有权利。', '© 2026 UK Visa AI Assistant. All rights reserved.')}</p>
                <p className="mt-2">
                  <a href="#" className="hover:text-white transition-colors">{t('ICP证: 京ICP备12345678号', 'ICP: Beijing ICP 12345678')}</a> | 
                  <a href="#" className="hover:text-white transition-colors ml-2">{t('营业执照', 'Business License')}</a> | 
                  <a href="#" className="hover:text-white transition-colors ml-2">{t('隐私政策', 'Privacy Policy')}</a>
                </p>
              </div>
            </div>
          </footer>
        </div>
      ) : (
        // 聊天界面
        <div className="flex flex-col h-[calc(100vh-4rem)]">
          <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-[#0f172a]">
            <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
              <div className="space-y-6">
                {messages.map((message) => (
                  <ChatMessage key={message.id} message={message} />
                ))}
                {isTyping && <TypingIndicator />}
                <div ref={messagesEndRef} />
              </div>

              {error && (
                <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
                  {error}
                </div>
              )}
            </div>
          </div>

          <ChatInput
            onSendMessage={handleSendMessage}
            onStop={handleStop}
            disabled={isLoading}
            isTyping={isTyping}
            suggestions={suggestions}
            onSuggestionClick={handleSuggestionClick}
          />
        </div>
      )}
    </div>
  );
};

export default ChatPage;
